import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Flashlight, AlertTriangle } from 'lucide-react';

// Morse code lookup
const MORSE = {
  'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.', 'G': '--.',
  'H': '....', 'I': '..', 'J': '.---', 'K': '-.-', 'L': '.-..', 'M': '--', 'N': '-.',
  'O': '---', 'P': '.--.', 'Q': '--.-', 'R': '.-.', 'S': '...', 'T': '-', 'U': '..-',
  'V': '...-', 'W': '.--', 'X': '-..-', 'Y': '-.--', 'Z': '--..',
  '0': '-----', '1': '.----', '2': '..---', '3': '...--', '4': '....-',
  '5': '.....', '6': '-....', '7': '--...', '8': '---..', '9': '----.',
  ' ': '/',
};

// SOS in morse: ... --- ...
const SOS_PATTERN = '... --- ...';

// Timing (ms)
const DOT_MS = 150;
const DASH_MS = DOT_MS * 3;
const INTRA_CHAR_GAP = DOT_MS;
const INTER_CHAR_GAP = DOT_MS * 3;
const WORD_GAP = DOT_MS * 7;

function textToMorse(text) {
  return text.toUpperCase().split('').map(ch => MORSE[ch] || '').filter(Boolean).join(' ');
}

function morseToTimings(morseStr) {
  const timings = [];
  for (let i = 0; i < morseStr.length; i++) {
    const ch = morseStr[i];
    if (ch === '.') {
      timings.push({ on: true, duration: DOT_MS });
      timings.push({ on: false, duration: INTRA_CHAR_GAP });
    } else if (ch === '-') {
      timings.push({ on: true, duration: DASH_MS });
      timings.push({ on: false, duration: INTRA_CHAR_GAP });
    } else if (ch === ' ') {
      // Replace the last intra-char gap with inter-char gap
      if (timings.length > 0) timings[timings.length - 1].duration = INTER_CHAR_GAP;
    } else if (ch === '/') {
      if (timings.length > 0) timings[timings.length - 1].duration = WORD_GAP;
    }
  }
  return timings;
}

export default function SOSFlasher({ open, onClose, c1, c2 }) {
  const [mode, setMode] = useState('sos'); // 'sos' | 'custom'
  const [customText, setCustomText] = useState('');
  const [isFlashing, setIsFlashing] = useState(false);
  const [screenOn, setScreenOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [currentMorse, setCurrentMorse] = useState(SOS_PATTERN);
  const [statusText, setStatusText] = useState('');
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const timerRef = useRef(null);
  const flashingRef = useRef(false);
  const abortRef = useRef(null);

  // Check torch availability on mount
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
        const track = stream.getVideoTracks()[0];
        const caps = track.getCapabilities?.();
        if (caps?.torch) {
          setTorchAvailable(true);
          streamRef.current = stream;
          trackRef.current = track;
        } else {
          track.stop();
          stream.getTracks().forEach(t => t.stop());
          setTorchAvailable(false);
        }
      } catch {
        setTorchAvailable(false);
      }
    })();
    return () => {
      stopFlashing();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        trackRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setTorch = useCallback(async (on) => {
    if (trackRef.current) {
      try {
        await trackRef.current.applyConstraints({ advanced: [{ torch: on }] });
      } catch { /* torch not available */ }
    }
  }, []);

  const stopFlashing = useCallback(() => {
    flashingRef.current = false;
    setIsFlashing(false);
    setScreenOn(false);
    setStatusText('');
    if (abortRef.current) abortRef.current.abort();
    if (timerRef.current) clearTimeout(timerRef.current);
    setTorch(false);
  }, [setTorch]);

  const sleep = (ms, signal) => new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => { clearTimeout(timer); reject(new Error('aborted')); });
    }
  });

  const startFlashing = useCallback(async (morseStr) => {
    stopFlashing();
    const ac = new AbortController();
    abortRef.current = ac;
    flashingRef.current = true;
    setIsFlashing(true);
    setCurrentMorse(morseStr);

    const timings = morseToTimings(morseStr);
    if (timings.length === 0) { stopFlashing(); return; }

    try {
      // Repeat pattern continuously
      while (flashingRef.current && !ac.signal.aborted) {
        for (const timing of timings) {
          if (!flashingRef.current || ac.signal.aborted) return;
          if (timing.on) {
            setScreenOn(true);
            setTorch(true);
            setStatusText(timing.duration === DOT_MS ? 'DOT' : 'DASH');
          } else {
            setScreenOn(false);
            setTorch(false);
            setStatusText('');
          }
          await sleep(timing.duration, ac.signal);
        }
        // Gap between repeats
        setScreenOn(false);
        setTorch(false);
        setStatusText('');
        await sleep(WORD_GAP, ac.signal);
      }
    } catch {
      // Aborted
    } finally {
      setScreenOn(false);
      setTorch(false);
    }
  }, [stopFlashing, setTorch]);

  const handleStartSOS = useCallback(() => {
    startFlashing(SOS_PATTERN);
  }, [startFlashing]);

  const handleStartCustom = useCallback(() => {
    if (!customText.trim()) return;
    const morse = textToMorse(customText.trim());
    if (!morse) return;
    startFlashing(morse);
  }, [customText, startFlashing]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="sos-flasher-overlay">
      {/* Full-screen flash background */}
      <div
        className="absolute inset-0 transition-colors"
        style={{
          backgroundColor: screenOn ? '#ffffff' : '#0a0a0f',
          transitionDuration: '50ms',
        }}
        data-testid="sos-flash-screen"
      />

      {/* Controls panel */}
      <div className="relative z-10 w-full max-w-sm mx-4 rounded-2xl border border-border/50 bg-background/95 backdrop-blur-xl p-5 shadow-2xl" data-testid="sos-controls">
        {/* Close button */}
        <button
          onClick={() => { stopFlashing(); onClose(); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-card border border-border/50 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          data-testid="sos-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Flashlight className="w-5 h-5" style={{ color: c1 }} />
          <h3 className="text-lg font-bold text-foreground">SOS Signal</h3>
        </div>

        {/* Torch status */}
        <div className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${torchAvailable ? 'bg-green-500' : 'bg-amber-500'}`} />
          {torchAvailable ? 'Torch available — will flash your camera light' : 'Screen flash mode — torch not available on this device'}
        </div>

        {/* Mode tabs */}
        <div className="flex gap-1 rounded-lg bg-card border border-border/40 p-1 mb-4" data-testid="sos-mode-tabs">
          <button
            onClick={() => { setMode('sos'); if (isFlashing) stopFlashing(); }}
            className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors ${mode === 'sos' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
            style={mode === 'sos' ? { backgroundColor: c1 } : {}}
            data-testid="sos-mode-sos"
          >
            SOS Signal
          </button>
          <button
            onClick={() => { setMode('custom'); if (isFlashing) stopFlashing(); }}
            className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors ${mode === 'custom' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
            style={mode === 'custom' ? { backgroundColor: c1 } : {}}
            data-testid="sos-mode-custom"
          >
            Custom Message
          </button>
        </div>

        {mode === 'sos' ? (
          <div className="space-y-3">
            <div className="text-center py-3">
              <p className="text-3xl font-mono font-bold tracking-[0.3em] text-foreground" data-testid="sos-morse-display">
                {SOS_PATTERN}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                3 short &middot; 3 long &middot; 3 short
              </p>
            </div>
            {statusText && (
              <p className="text-center text-sm font-bold" style={{ color: c1 }} data-testid="sos-status">{statusText}</p>
            )}
            {!isFlashing ? (
              <Button
                onClick={handleStartSOS}
                className="w-full h-12 text-white font-bold text-sm rounded-xl"
                style={{ backgroundColor: '#ef4444' }}
                data-testid="sos-start-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Start SOS Signal
              </Button>
            ) : (
              <Button
                onClick={stopFlashing}
                variant="outline"
                className="w-full h-12 font-bold text-sm rounded-xl border-red-500/50 text-red-400"
                data-testid="sos-stop-btn"
              >
                Stop Signal
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-foreground mb-1.5 block">Type your message</label>
              <Input
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder="e.g. HELP"
                className="bg-card border-border text-sm"
                maxLength={50}
                data-testid="sos-custom-input"
              />
            </div>
            {customText.trim() && (
              <div className="rounded-lg bg-card/50 border border-border/30 p-3" data-testid="sos-morse-preview">
                <p className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">Morse code</p>
                <p className="text-sm font-mono font-semibold text-foreground tracking-wider break-all">
                  {textToMorse(customText.trim())}
                </p>
              </div>
            )}
            {statusText && (
              <p className="text-center text-sm font-bold" style={{ color: c1 }} data-testid="sos-custom-status">{statusText}</p>
            )}
            {!isFlashing ? (
              <Button
                onClick={handleStartCustom}
                disabled={!customText.trim()}
                className="w-full h-12 text-white font-bold text-sm rounded-xl disabled:opacity-40"
                style={{ backgroundColor: c1 }}
                data-testid="sos-custom-start-btn"
              >
                <Flashlight className="w-4 h-4 mr-2" />
                Flash Message
              </Button>
            ) : (
              <Button
                onClick={stopFlashing}
                variant="outline"
                className="w-full h-12 font-bold text-sm rounded-xl"
                style={{ borderColor: `${c1}50`, color: c1 }}
                data-testid="sos-custom-stop-btn"
              >
                Stop Signal
              </Button>
            )}
          </div>
        )}

        {/* Safety note */}
        <p className="text-[10px] text-muted-foreground mt-4 text-center leading-relaxed">
          International distress signal. Use only in genuine emergencies.
          {!torchAvailable && ' Your screen will flash bright white for the signal.'}
        </p>
      </div>
    </div>
  );
}
